import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { OverlayPanel } from 'primereact/overlaypanel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

// Components
import Loader from '../../components/Loader';
import MergeCompanyModal from '../../components/popup/MergeCompanyModal';
import AddEditMasterCompanyModal from '../../components/popup/AddEditMasterCompanyModal';

// Actions & APIs
import { getInterviewTechnology } from '../../redux/actions/hr';
import { getInterviewTechnologyApi } from '../../redux/apis/hr';
import { getCompanyReportApi } from '../../redux/apis/company';

// Constants & Utilities
import { CALL_STATUS_CONFIG, DEBOUNCE_TIMEOUT } from '../../constants/common';
import { isDarkMode } from '../../utils/common';
import { debounce } from '../../utils/debounce';

const DATE_PILLS = ['All', 'Custom Date'];
const COMPANY_TYPE_PILLS = ['All', 'Merged'];

const CompanyReport = () => {
  const dispatch = useDispatch();

  // DOM Refs for popups / overlays
  const inlineCalendarRef = useRef(null);
  const stickyCalendarRef = useRef(null);
  const inlineMultiSelectRef = useRef(null);
  const stickyMultiSelectRef = useRef(null);
  const inlineStatusSelectRef = useRef(null);
  const stickyStatusSelectRef = useRef(null);
  const statusPopoverRef = useRef(null);

  // Redux Technologies
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const [localTechList, setLocalTechList] = useState([]);

  // Filter States
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activePill, setActivePill] = useState('All');
  const [customRange, setCustomRange] = useState([new Date(), new Date()]);
  const [companyTypeFilter, setCompanyTypeFilter] = useState('All');

  // Technology Filter
  const [techFilter, setTechFilter] = useState([]);
  const [techFilterPending, setTechFilterPending] = useState([]);

  // Call Status Filter
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusFilterPending, setStatusFilterPending] = useState([]);

  // UI state
  const [showStickyFilter, setShowStickyFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  // Table Data & Pagination
  const [companyData, setCompanyData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [sortBy, setSortBy] = useState('lastUpdated');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // Active status popover state
  const [activePopoverRow, setActivePopoverRow] = useState(null);

  // Modal States
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSourceCompanies, setMergeSourceCompanies] = useState([]);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // ── Debounced Search ──────────────────────────────────────────────
  const debouncedSetSearch = useMemo(
    () =>
      debounce((val) => {
        setDebouncedSearch(val);
        setPage(1);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    debouncedSetSearch(val);
  };

  // ── Debounced Filters ────────────────────────────────────────────
  const debouncedSetTechFilter = useMemo(
    () =>
      debounce((value) => {
        setTechFilter(value);
        setPage(1);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  const debouncedSetStatusFilter = useMemo(
    () =>
      debounce((value) => {
        setStatusFilter(value);
        setPage(1);
      }, DEBOUNCE_TIMEOUT),
    []
  );

  // Cleanup debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel?.();
      debouncedSetTechFilter.cancel?.();
      debouncedSetStatusFilter.cancel?.();
    };
  }, [debouncedSetSearch, debouncedSetTechFilter, debouncedSetStatusFilter]);

  const handleTechFilterChange = (e) => {
    setTechFilterPending(e.value);
    debouncedSetTechFilter(e.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilterPending(e.value);
    debouncedSetStatusFilter(e.value);
  };

  // Technologies List combining Redux & API
  const effectiveTechnologies = useMemo(() => {
    if (technologyListData && Array.isArray(technologyListData) && technologyListData.length > 0) {
      return technologyListData;
    }
    return localTechList || [];
  }, [technologyListData, localTechList]);

  const techOptions = useMemo(() => {
    if (!effectiveTechnologies || !Array.isArray(effectiveTechnologies)) return [];
    return effectiveTechnologies.map((tech) => ({
      label: tech.Technology || tech.name || tech.label || '',
      value: tech.ID !== undefined ? tech.ID : tech.id !== undefined ? tech.id : tech.value
    }));
  }, [effectiveTechnologies]);

  const statusOptions = useMemo(() => {
    return CALL_STATUS_CONFIG.map((item) => ({
      label: item.label,
      value: item.code
    }));
  }, []);

  // Fetch Technologies on Mount
  useEffect(() => {
    dispatch(getInterviewTechnology());
    getInterviewTechnologyApi()
      .then((res) => {
        if (res?.data?.data && Array.isArray(res.data.data)) {
          setLocalTechList(res.data.data);
        }
      })
      .catch((err) => console.error('Error fetching technologies:', err));
  }, [dispatch]);

  // ═════════════════════════════════════════════════════════════════
  // ── FETCH COMPANY REPORT DATA ────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════
  const fetchCompanyData = useCallback(async () => {
    setLoading(true);

    let startDate = null;
    let endDate = null;
    if (activePill === 'Custom Date') {
      const range = customRange && customRange[0] ? customRange : [new Date(), new Date()];
      startDate = dayjs(range[0]).format('YYYY-MM-DD');
      endDate = range[1] ? dayjs(range[1]).format('YYYY-MM-DD') : startDate;
    }

    try {
      const response = await getCompanyReportApi({
        search: debouncedSearch || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        isMerge: companyTypeFilter === 'Merged' ? true : false,
        technology: techFilter?.length > 0 ? techFilter.join(',') : undefined,
        callStatus: statusFilter?.length > 0 ? statusFilter.join(',') : undefined,
        page,
        size,
        sortBy,
        sortOrder
      });

      if (response?.data?.success || response?.status === 200 || response?.status === 304) {
        const resBody = response.data || {};

        // 1. Parse rows array
        const rows = Array.isArray(resBody.data)
          ? resBody.data
          : Array.isArray(resBody)
          ? resBody
          : Array.isArray(resBody.data?.data)
          ? resBody.data.data
          : Array.isArray(resBody.rows)
          ? resBody.rows
          : Array.isArray(resBody.list)
          ? resBody.list
          : [];

        // 2. Parse total records from pagination metadata
        const total =
          resBody.pagination?.totalRecords ??
          resBody.pagination?.totalCount ??
          resBody.pagination?.count ??
          resBody.totalRecords ??
          resBody.totalCount ??
          resBody.count ??
          resBody.data?.pagination?.totalRecords ??
          resBody.data?.totalRecords ??
          resBody.data?.totalCount ??
          (rows[0]?.totalCount ? Number(rows[0].totalCount) : null) ??
          (rows[0]?.fullCount ? Number(rows[0].fullCount) : null) ??
          rows.length;

        setCompanyData(rows);
        setTotalRecords(Number(total) || 0);
      }
    } catch (error) {
      setCompanyData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    debouncedSearch,
    activePill,
    customRange,
    companyTypeFilter,
    techFilter,
    statusFilter,
    page,
    size,
    sortBy,
    sortOrder
  ]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  // ── Table Sorting Handler (3-state cycle on Company Name) ─────────
  const handleSort = (e) => {
    if (e.sortField === 'name') {
      if (sortBy !== 'name') {
        // 1st click: Sort by name ASC
        setSortBy('name');
        setSortOrder('ASC');
      } else if (sortOrder === 'ASC') {
        // 2nd click: Sort by name DESC
        setSortBy('name');
        setSortOrder('DESC');
      } else {
        // 3rd click: Remove sort on company name, revert to default lastUpdated ASC
        setSortBy('lastUpdated');
        setSortOrder('DESC');
      }
    } else {
      // Reverted or removed sort field
      setSortBy('lastUpdated');
      setSortOrder('DESC');
    }
    setPage(1);
  };

  // Floating sticky filter bar handling on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyFilter(window.scrollY > 80);
      [inlineCalendarRef, stickyCalendarRef].forEach((ref) => {
        if (ref.current) {
          if (typeof ref.current.hideOverlay === 'function') ref.current.hideOverlay();
          else if (typeof ref.current.hide === 'function') ref.current.hide();
        }
      });
      [
        inlineMultiSelectRef,
        stickyMultiSelectRef,
        inlineStatusSelectRef,
        stickyStatusSelectRef
      ].forEach((ref) => {
        if (ref.current) {
          if (typeof ref.current.hideOverlay === 'function') ref.current.hideOverlay();
          else if (typeof ref.current.hide === 'function') ref.current.hide();
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Actions & Handlers ──────────────────────────────────────────
  const handleOpenBulkMerge = () => {
    if (selectedCompanies.length < 1) {
      toast.warning('Please select at least 1 company to merge.');
      return;
    }
    setMergeSourceCompanies(selectedCompanies);
    setShowMergeModal(true);
  };

  const handleOpenEditCompany = (company) => {
    setEditingCompany(company);
    setShowAddEditModal(true);
  };

  const handleMergeSuccess = () => {
    setSelectedCompanies([]);
    fetchCompanyData();
  };

  // Check if a company is a master/merged company
  const isMasterCompany = useCallback((row) => {
    if (!row) return false;
    const aliases = Array.isArray(row.aliases) ? row.aliases : [];
    return aliases.length > 0 || Boolean(row.isMaster);
  }, []);

  // ── Table Column Renderers ──────────────────────────────────────
  const renderCompanyName = (row) => {
    const aliases = Array.isArray(row.aliases) ? row.aliases : [];
    const aliasCount = aliases.length;
    const badgeText = aliasCount > 9 ? '9+' : aliasCount;

    return (
      <div className="rpt-company-cell">
        <span className="rpt-company-cell__name">{row.name}</span>
        {aliasCount > 0 && (
          <span
            className="rpt-alias-count-badge"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditCompany(row);
            }}>
            {badgeText}
          </span>
        )}
      </div>
    );
  };

  const renderTechnologies = (row) => {
    const techs = row.technologies || [];
    if (techs.length === 0) return <span>--</span>;

    return (
      <ul className="technology-list m-0 p-0">
        {techs.slice(0, 3).map((t, idx) => (
          <li
            key={idx}
            className="technology-badge"
            style={{
              backgroundColor: isDarkMode()
                ? t.darkModeColorCode || t.colorCode || '#6c757d'
                : t.colorCode || '#6c757d'
            }}>
            {t.name || t.Technology}
            {t.count !== undefined && <strong className="ml-1">({t.count})</strong>}
          </li>
        ))}
      </ul>
    );
  };

  const renderTotalCandidates = (row) => {
    return <span className="rpt-candidate-count-badge">{row.totalCandidates || 0}</span>;
  };

  // Top 2 Call Statuses + '+X more' Popover Renderer
  const renderCallStatusBreakdown = (row) => {
    const counts = row.callStatusCounts || {};
    const countEntries = Object.entries(counts)
      .map(([code, count]) => {
        const numCode = Number(code);
        const config = CALL_STATUS_CONFIG.find((c) => c.code === numCode || c.label === code) || {
          label: code,
          color: '#6c757d',
          code: numCode
        };
        return {
          code: numCode,
          label: config.label,
          color: config.color,
          count: Number(count)
        };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);

    if (countEntries.length === 0) return <span>--</span>;

    const topTwo = countEntries.slice(0, 2);
    const remaining = countEntries.slice(2);

    return (
      <div className="rpt-status-summary-cell">
        {topTwo.map((st) => (
          <span
            key={st.code || st.label}
            className="rpt-call-status-pill"
            style={{
              backgroundColor: `${st.color}18`,
              color: st.color,
              borderColor: `${st.color}40`
            }}>
            {st.label}: <strong>{st.count}</strong>
          </span>
        ))}

        {remaining.length > 0 && (
          <button
            type="button"
            className="rpt-status-more-btn"
            onClick={(e) => {
              setActivePopoverRow(row);
              statusPopoverRef.current.toggle(e);
            }}>
            +{remaining.length} more
          </button>
        )}
      </div>
    );
  };

  // ── Filter Bar Renderer ─────────────────────────────────────────

  const renderFilterBar = (isSticky = false) => (
    <div
      className={`rpt-filter-bar ${isSticky ? 'rpt-filter-bar--sticky' : ''} ${
        isSticky && showStickyFilter ? 'rpt-filter-bar--sticky-show' : ''
      }`}>
      {/* ── Search Input ── */}
      <div className="p-input-icon-left" style={{ minWidth: '240px' }}>
        <i className="pi pi-search" />
        <InputText
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Search name or alias (e.g. TCS)..."
          className="p-inputtext-sm w-full"
        />
      </div>

      {/* ── Date Range Pills ── */}
      <div className="rpt-pill-group">
        {DATE_PILLS.map((pill) => (
          <button
            key={pill}
            type="button"
            className={`rpt-pill ${activePill === pill ? 'rpt-pill--active' : ''}`}
            onClick={() => {
              setActivePill(pill);
              setPage(1);
            }}>
            {pill}
          </button>
        ))}
      </div>

      {/* ── Custom Date Calendar ── */}
      {activePill === 'Custom Date' && (
        <Calendar
          ref={isSticky ? stickyCalendarRef : inlineCalendarRef}
          value={customRange}
          onChange={(e) => {
            setCustomRange(e.value || [new Date(), new Date()]);
            setPage(1);
          }}
          selectionMode="range"
          readOnlyInput
          placeholder="Select date range"
          className="rpt-calendar"
          dateFormat="dd/mm/yy"
          showIcon
        />
      )}

      {/* ── All / Merged Company Toggle ── */}
      <div className="rpt-pill-group">
        {COMPANY_TYPE_PILLS.map((type) => (
          <button
            key={type}
            type="button"
            className={`rpt-pill ${companyTypeFilter === type ? 'rpt-pill--active' : ''}`}
            onClick={() => {
              setCompanyTypeFilter(type);
              setPage(1);
            }}>
            {type}
          </button>
        ))}
      </div>

      {/* ── Technology Filter ── */}
      <MultiSelect
        ref={isSticky ? stickyMultiSelectRef : inlineMultiSelectRef}
        value={techFilterPending}
        options={techOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleTechFilterChange}
        placeholder="Filter by Technology"
        display="chip"
        filter
        maxSelectedLabels={2}
        className="rpt-multiselect"
      />

      {/* ── Call Status Filter ── */}
      <MultiSelect
        ref={isSticky ? stickyStatusSelectRef : inlineStatusSelectRef}
        value={statusFilterPending}
        options={statusOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleStatusFilterChange}
        placeholder="Filter by Status"
        display="chip"
        filter
        maxSelectedLabels={2}
        className="rpt-multiselect"
      />
    </div>
  );

  return (
    <div className="rpt-call-report">
      {loading && <Loader />}

      {/* ── Static Filter Bar ── */}
      {renderFilterBar(false)}

      {/* ── Floating Sticky Filter Bar ── */}
      {renderFilterBar(true)}

      {/* ── Table Card ── */}
      <div className="rpt-tabs-card rpt-tabs-card--padded">
        <div className="rpt-table-header-block">
          <div className="rpt-table-header-block__left">
            <i className="pi pi-building rpt-table-header-block__icon" />
            <span className="rpt-table-header-block__title">Company Directory & Analytics</span>
            {totalRecords > 0 && <Badge value={totalRecords} severity="info" className="ml-2" />}
          </div>
        </div>

        {/* ── Responsive DataTable with Checkboxes ── */}
        <div className="white-box custom-table-wrapper p-0 overflow-hidden">
          <DataTable
            value={companyData}
            selection={selectedCompanies}
            onSelectionChange={(e) =>
              setSelectedCompanies((e.value || []).filter((c) => !isMasterCompany(c)))
            }
            isDataSelectable={(e) => !isMasterCompany(e.data)}
            rowClassName={(data) => (isMasterCompany(data) ? 'rpt-row--master' : '')}
            dataKey="id"
            responsiveLayout="scroll"
            size="small"
            showGridlines
            stripedRows
            lazy={true}
            paginator={true}
            rows={size}
            totalRecords={totalRecords}
            first={(page - 1) * size}
            onPage={(e) => {
              setPage(e.page + 1);
              setSize(e.rows);
            }}
            sortField={sortBy === 'name' ? 'name' : null}
            sortOrder={sortBy === 'name' ? (sortOrder === 'ASC' ? 1 : -1) : null}
            onSort={handleSort}
            removableSort
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} companies"
            emptyMessage="No companies found matching the active filters.">
            <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
            <Column
              field="name"
              header="Company Name"
              body={renderCompanyName}
              sortable
              headerClassName="rpt-header-company-name"
              style={{ minWidth: '240px' }}
            />
            <Column
              header="Top Technologies"
              body={renderTechnologies}
              style={{ minWidth: '200px' }}
            />
            <Column
              header="Total Candidates"
              body={renderTotalCandidates}
              style={{ width: '150px', textAlign: 'center' }}
            />
            <Column
              header="Call Status Breakdown"
              body={renderCallStatusBreakdown}
              style={{ minWidth: '280px' }}
            />
          </DataTable>
        </div>
      </div>

      {/* ── Status Popover for '+X more' Breakdown ── */}
      <OverlayPanel ref={statusPopoverRef} className="rpt-status-overlay-panel shadow-3">
        {activePopoverRow && (
          <div className="rpt-status-popover-content">
            <div className="rpt-status-popover-content__title">
              {activePopoverRow.name} — Call Statuses
            </div>
            {Object.entries(activePopoverRow.callStatusCounts || {})
              .map(([code, count]) => {
                const numCode = Number(code);
                const config = CALL_STATUS_CONFIG.find(
                  (c) => c.code === numCode || c.label === code
                ) || {
                  label: code,
                  color: '#6c757d'
                };
                return {
                  code: numCode,
                  label: config.label,
                  color: config.color,
                  count: Number(count)
                };
              })
              .filter((item) => item.count > 0)
              .sort((a, b) => b.count - a.count)
              .map((item) => (
                <div key={item.code || item.label} className="rpt-status-popover-content__row">
                  <span
                    className="rpt-call-status-pill"
                    style={{
                      backgroundColor: `${item.color}18`,
                      color: item.color,
                      borderColor: `${item.color}40`
                    }}>
                    <span
                      className="rpt-call-status-pill__dot"
                      style={{ backgroundColor: item.color }}
                    />
                    {item.label}
                  </span>
                  <span className="rpt-status-popover-content__count">{item.count}</span>
                </div>
              ))}
          </div>
        )}
      </OverlayPanel>

      {/* ── Floating Bulk Action Bar (Slides up when >= 1 selected) ── */}
      <div
        className={`rpt-floating-bar ${
          selectedCompanies.length >= 1 ? 'rpt-floating-bar--visible' : ''
        }`}>
        <div className="rpt-floating-bar__actions">
          <Button
            label={`Merge (${selectedCompanies.length})`}
            icon="pi pi-directions"
            className="rpt-floating-bar__merge-btn"
            onClick={handleOpenBulkMerge}
          />
          <Button
            label="Clear"
            icon="pi pi-times"
            className="rpt-floating-bar__clear-btn"
            onClick={() => setSelectedCompanies([])}
          />
        </div>
      </div>

      {/* ── Merge Consolidation Modal ── */}
      <MergeCompanyModal
        show={showMergeModal}
        onHide={() => setShowMergeModal(false)}
        sourceCompanies={mergeSourceCompanies}
        onMergeSuccess={handleMergeSuccess}
      />

      {/* ── Edit Master Company Modal ── */}
      <AddEditMasterCompanyModal
        show={showAddEditModal}
        onHide={() => {
          setShowAddEditModal(false);
          setEditingCompany(null);
        }}
        company={editingCompany}
        onSuccess={fetchCompanyData}
      />
    </div>
  );
};

export default CompanyReport;
